import $ from 'jquery';
import { serializeArray } from '../functions/helperFunctions';
import { pageWidth } from '../functions/windowSizeFunctions';
import { sanitizeHTML } from '../functions/sanitize';

export default class WCProductCustomDateSelector {
    constructor() {
        this.activeSelect = null;
        this.activeVariation = 0;
        this.selectToShow = [];
        this.typeTimeout = undefined;
        this.dateTimeout = undefined;
        this.typeHighlighted = null;
        this.dateHighlighted = null;

        // a date chosen from the dates list / calendar that waits for the
        // date control of its variation: null or { variationId, slug }
        this.pendingChoice = null;

        this.addToCartBtn = document.querySelector('.jsAddToCartBtn');

        this.wcProductActions = document.querySelector('.jsWcProductActions');
        this.variationForm = document.querySelector('.jsVariableProductForm');
        this.dynamicProductPrice = document.querySelector(
            '.jsDynamicVariableProductPrice',
        );
        this.dynamicProductPriceExtra = document.querySelector(
            '.jsVariableProductPriceExtra',
        );

        this.selectProductFromListForm = [
            ...document.querySelectorAll('.jsSelectProductFromListForm'),
        ];

        this.selectProductFromListItem = [
            ...document.querySelectorAll('.jsSelectProductFromListItem'),
        ];

        this.productListCalendar = document.querySelector('.jsTuiCalendar');

        this.dateSelectorWrap = this.variationForm
            ? this.variationForm.querySelector('.jsDateSelectorWrap')
            : null;

        this.dateSelectorData =
            this.dateSelectorWrap &&
            this.dateSelectorWrap?.dataset?.dateSelector
                ? JSON.parse(this.dateSelectorWrap.dataset.dateSelector)
                : {};

        // price label data per variation - used for a variation without dates
        this.variationPrices =
            this.dateSelectorWrap &&
            this.dateSelectorWrap?.dataset?.variationPrices
                ? JSON.parse(this.dateSelectorWrap.dataset.variationPrices)
                : {};

        // the label and badge as the server rendered them - restored on reset
        this.initialPriceHtml = this.dynamicProductPrice
            ? this.dynamicProductPrice.innerHTML
            : '';
        this.initialPriceExtraHtml = this.dynamicProductPriceExtra
            ? this.dynamicProductPriceExtra.innerHTML
            : '';

        this.dateSelectorInputs = this.dateSelectorWrap
            ? [
                  ...this.dateSelectorWrap.querySelectorAll(
                      '.jsDateSelectorInput',
                  ),
              ]
            : null;

        this.dateSelector = this.dateSelectorWrap
            ? this.dateSelectorWrap.querySelector('.jsDateSelector')
            : null;

        if (this.dateSelector && Object.keys(this.dateSelectorData).length)
            this.events();
    }

    events() {
        $(this.variationForm).on('show_variation', (event, variation) => {
            this.initializeSelectDataOnChange(variation);
        });

        // The form resolved to a variation. Its date control is built later,
        // by show_variation - WooCommerce may fire that event after a delay
        $(this.variationForm).on('found_variation', (event, variation) => {
            this.onFoundVariation(variation);
        });

        // WooCommerce fires reset_data when the selection is cleared and
        // whenever not all attributes are chosen - the reset is idempotent
        $(this.variationForm).on('reset_data', () => {
            this.pendingChoice = null;
            this.resetSelection();
        });

        if (this.selectProductFromListForm.length) {
            this.selectProductFromListForm.forEach((listForm) =>
                listForm.addEventListener(
                    'submit',
                    this.updateVariationFromList.bind(this),
                ),
            );
        }

        if (this.selectProductFromListItem.length) {
            this.selectProductFromListItem.forEach((item) =>
                item.addEventListener('click', (e) => {
                    this.submitFormOnItemClick(e);
                }),
            );
        }

        if (this.productListCalendar) {
            this.productListCalendar.addEventListener(
                'updateVariationFromCalendar',
                this.updateVariationFromCalendar.bind(this),
            );
        }

        if (this.addToCartBtn) {
            this.addToCartBtn.addEventListener(
                'click',
                this.checkIfCanAddToCart.bind(this),
            );
        }
    }

    // The variation the variations form currently holds (0 = none). The
    // query is scoped to the form - the list forms have inputs of that name
    getFormVariationInput() {
        return this.variationForm
            ? this.variationForm.querySelector('input[name="variation_id"]')
            : null;
    }

    getFormVariationId() {
        const input = this.getFormVariationInput();

        return input ? this.toVariationId(input.value) : 0;
    }

    toVariationId(value) {
        const id = parseInt(value, 10);

        return Number.isInteger(id) && id > 0 ? id : 0;
    }

    onFoundVariation(variation) {
        const variationId = this.toVariationId(variation?.variation_id);

        // a choice made for another variation cannot take effect any more
        if (
            this.pendingChoice &&
            this.pendingChoice.variationId !== variationId
        ) {
            this.pendingChoice = null;
        }

        // the date in the form must belong to the form's variation: until
        // the new variation's control is shown the date fields stay empty
        if (variationId !== this.toVariationId(this.activeVariation)) {
            this.clearAllHiddenInputs();
        }
    }

    initializeSelectDataOnChange(data) {
        const variationId = this.toVariationId(data?.variation_id);

        // a late event for a variation the form no longer holds
        if (
            this.getFormVariationInput() &&
            this.getFormVariationId() !== variationId
        ) {
            return;
        }

        const pendingSlug =
            this.pendingChoice && this.pendingChoice.variationId === variationId
                ? this.pendingChoice.slug
                : null;

        this.pendingChoice = null;

        // the date to select: the chosen one, else the one already selected
        // in this variation's control, else the first
        let slugToSelect = pendingSlug;

        if (
            !slugToSelect &&
            this.activeSelect &&
            this.toVariationId(this.activeVariation) === variationId
        ) {
            slugToSelect = this.activeSelect.value;
        }

        this.selectToShow = data.variation_id
            ? this.dateSelectorData[data.variation_id]
            : [];

        // A variation without dates: no date control, no date data, the
        // button disabled and the variation's own price in the label
        if (!this.selectToShow || !this.selectToShow.length) {
            this.resetSelection();
            this.activeVariation = data.variation_id ? data.variation_id : 0;

            const variationPrice = data.variation_id
                ? this.variationPrices[data.variation_id]
                : null;

            if (variationPrice) this.setPriceLabel(variationPrice);

            return;
        }

        this.activeVariation = data.variation_id ? data.variation_id : 0;

        if (this.activeSelect) {
            this.destroyCurrentSelect(this.activeSelect);
            this.activeSelect = null;
            this.clearAllHiddenInputs();
        }

        let startOption = null;
        let startIdx = slugToSelect
            ? this.selectToShow.findIndex(
                  (selOpt) => selOpt.slugified_name === slugToSelect,
              )
            : 0;

        if (startIdx < 0) startIdx = 0;

        this.dateSelector.innerHTML = '';

        const select = document.createElement('select');
        select.classList.add('jsCourseDateSelect');

        this.selectToShow.forEach((selOpt, idx) => {
            const option = document.createElement('option');
            option.value = selOpt.slugified_name;
            option.innerText = selOpt.name;
            if (idx === startIdx) {
                startOption = selOpt;
                option.setAttribute('selected', 'selected');
            }

            select.insertAdjacentElement('beforeend', option);
        });

        this.dateSelector.insertAdjacentElement('afterbegin', select);

        this.activeSelect = select;

        this.initializeSelectField(select);
        this.setExtraVariationData(startOption);

        // a date chosen from the list / calendar: same feedback as when the
        // date is changed in a control that is already there
        if (pendingSlug) {
            this.highlightDateControl();

            if (pageWidth() < 1024) this.scrollToActions();
        }
    }

    initializeSelectField(newSelect) {
        // Set up a Select2 control
        $(newSelect).select2({
            minimumResultsForSearch: -1,
        });

        // Bind an event
        $(newSelect).on('select2:select', (e) => {
            this.getCurrentDateVariation(e);
        });
    }

    // Back to the state of the freshly loaded page: no date control, empty
    // date inputs, disabled button, the server-rendered label, no badge
    resetSelection() {
        if (this.activeSelect) {
            this.destroyCurrentSelect(this.activeSelect);
            this.activeSelect = null;
        }

        this.selectToShow = [];
        this.activeVariation = 0;

        if (this.dateSelector) this.dateSelector.innerHTML = '';

        this.clearAllHiddenInputs();

        if (this.dynamicProductPrice) {
            this.dynamicProductPrice.innerHTML = sanitizeHTML(
                this.initialPriceHtml,
            );
        }

        if (this.dynamicProductPriceExtra) {
            this.dynamicProductPriceExtra.innerHTML = sanitizeHTML(
                this.initialPriceExtraHtml,
            );
            this.dynamicProductPriceExtra.style.display = 'none';
        }
    }

    destroyCurrentSelect(currentSelect) {
        // Destroy Select2
        $(currentSelect).select2('destroy');

        // Unbind the event
        $(currentSelect).off('select2:select');
    }

    getCurrentDateVariation(e) {
        const currentSelect = e.currentTarget;

        if (this.activeSelect !== currentSelect) return;

        const seletedOption = currentSelect.selectedIndex;

        const dataToSet = this.selectToShow[seletedOption];

        this.setExtraVariationData(dataToSet);
    }

    setExtraVariationData(data) {
        if (!this.dateSelectorInputs.length && !Object.keys(data).length)
            return;

        this.dateSelectorInputs.forEach((input) => {
            switch (input.name) {
                case 'course_date_name':
                    input.value = data.name;
                    break;
                case 'course_date_start':
                    input.value = data.start_date;
                    break;
                case 'course_date_end':
                    input.value = data.end_date;
                    break;
                case 'course_term':
                    input.value = data.term_slug;
                    break;
                case 'course_term_name':
                    input.value = data.term_name;
                    break;
                case 'course_location':
                    input.value = data.location_name;
                    break;
                case 'course_promo_price_active':
                    input.value = data.promo_price_active;
                    break;
                case 'course_price':
                    input.value = data.promo_price_active
                        ? data.promo_price
                        : data.price;
                    break;
            }
        });

        this.setPriceLabel(data);

        if (this.addToCartBtn) {
            this.addToCartBtn.classList.remove('is-disabled');
        }
    }

    // Writes the price label and the sale badge (date row or variation)
    setPriceLabel(data) {
        if (this.dynamicProductPrice) {
            this.dynamicProductPrice.innerHTML = sanitizeHTML(data.price_html);
        }

        if (this.dynamicProductPriceExtra) {
            this.dynamicProductPriceExtra.innerHTML = '';

            if (
                data.price_badge &&
                data.price_badge_text &&
                data.price_base_html
            ) {
                const isBf = window.global.is_bf ? 'is-bf' : '';
                this.dynamicProductPriceExtra.innerHTML = sanitizeHTML(`
                    <span class="price-badge price-badge--${data.price_badge} ${isBf}">${data.price_badge_text}</span>
                    <del>${data.price_base_html}</del>
                `);

                this.dynamicProductPriceExtra.removeAttribute('style');
            } else {
                this.dynamicProductPriceExtra.style.display = 'none';
            }
        }
    }

    clearAllHiddenInputs() {
        if (!this.dateSelectorInputs.length) return;

        this.dateSelectorInputs.forEach((input) => {
            input.value = '';
        });

        if (this.addToCartBtn) {
            this.addToCartBtn.classList.add('is-disabled');
        }
    }

    checkIfCanAddToCart(e) {
        const btn = e.currentTarget;

        if (!btn) return;

        if (btn.classList.contains('is-disabled')) {
            e.preventDefault();

            window.alert(window.global.no_date_text);
        }
    }

    updateVariationFromList(e) {
        e.preventDefault();

        const selectedForm = e.currentTarget;

        if (!selectedForm) return;

        const serializedData = serializeArray(selectedForm);

        const serializedDataVarId = serializedData.filter(
            (data) => data.name === 'variation_id',
        );

        const serializedDataSelVal = serializedData.filter(
            (data) => data.name === 'course_slugified_name',
        );

        const serializedDataVarTerm = serializedData.filter(
            (data) => data.name === 'course_term_name',
        );

        const updateData = {
            variation_id:
                serializedDataVarId.length && serializedDataVarId[0]?.value
                    ? parseInt(serializedDataVarId[0].value, 10)
                    : null,

            course_slugified_name:
                serializedDataSelVal.length && serializedDataSelVal[0]?.value
                    ? serializedDataSelVal[0].value
                    : '',

            course_term_name:
                serializedDataVarTerm.length && serializedDataVarTerm[0]?.value
                    ? serializedDataVarTerm[0].value
                    : '',
        };

        this.updateVariation(updateData);
    }

    updateVariationFromCalendar(e) {
        const updateData = e?.detail;

        if (!updateData) return;

        this.updateVariation(updateData);
    }

    // A date chosen from the dates list or the calendar
    updateVariation(data) {
        const variationId = this.toVariationId(data?.variation_id);
        const slug = data?.course_slugified_name;

        if (!variationId || !slug) return;

        const variationDates = this.dateSelectorData[variationId];

        if (
            !Array.isArray(variationDates) ||
            !variationDates.some((selOpt) => selOpt.slugified_name === slug)
        ) {
            return;
        }

        const formVariationId = this.getFormVariationId();

        // The control of the chosen variation is shown and the form holds
        // that variation: select the date in it
        if (
            this.activeSelect &&
            this.toVariationId(this.activeVariation) === variationId &&
            formVariationId === variationId
        ) {
            this.pendingChoice = null;

            if (this.activeSelect.value !== slug) {
                this.updateDateSelect(this.activeSelect, slug);
            }

            return;
        }

        // Otherwise the choice waits for the show_variation event that
        // builds the control of its variation (the last choice wins)
        this.pendingChoice = { variationId, slug };

        if (formVariationId === variationId) return;

        this.selectVariationType(data?.course_term_name);

        // the form did not resolve to the chosen variation - drop the choice
        if (this.getFormVariationId() !== variationId) {
            this.pendingChoice = null;
        }
    }

    // Sets the participation type through its radio; the types module
    // writes the attribute and WooCommerce resolves the variation
    selectVariationType(termSlug) {
        if (!termSlug) return;

        const radio = [
            ...document.querySelectorAll(
                '.jsVariationSelection input[type="radio"]',
            ),
        ].find((typeRadio) => typeRadio.value === termSlug);

        if (!radio) return;

        if (radio.checked) {
            // checked although the form does not hold its variation (after a
            // keyboard reset) - a click would change nothing
            radio.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
            radio.click();
        }

        this.highlightTypeLabel(radio.nextElementSibling);
    }

    // The highlight timers remove the class from the element it was put on
    highlightTypeLabel(label) {
        if (!label) return;

        if (this.typeTimeout !== undefined) {
            clearTimeout(this.typeTimeout);

            if (this.typeHighlighted) {
                this.typeHighlighted.classList.remove('has-changed');
            }
        }

        label.classList.add('has-changed');
        this.typeHighlighted = label;

        this.typeTimeout = window.setTimeout(() => {
            label.classList.remove('has-changed');
        }, 10000);
    }

    highlightDateControl() {
        const control = this.activeSelect
            ? this.activeSelect.nextElementSibling
            : null;

        if (!control) return;

        if (this.dateTimeout !== undefined) {
            clearTimeout(this.dateTimeout);

            if (this.dateHighlighted) {
                this.dateHighlighted.classList.remove('has-changed');
            }
        }

        control.classList.add('has-changed');
        this.dateHighlighted = control;

        this.dateTimeout = window.setTimeout(() => {
            control.classList.remove('has-changed');
        }, 10000);
    }

    submitFormOnItemClick(e) {
        const currentPageWidth = pageWidth();

        if (currentPageWidth < 1024) {
            const clickedItem = e.currentTarget;

            const itemFormBtn = clickedItem.querySelector(
                '.jsSelectProductFromListForm .jsSelectProductFromListSubmit',
            );

            if (!itemFormBtn) return;

            itemFormBtn.click();
        }
    }

    updateDateSelect(select, value) {
        const currentPageWidth = pageWidth();

        $(select).val(value).trigger('change');
        $(select).val(value).trigger('select2:select');

        if (currentPageWidth < 1024) this.scrollToActions();

        this.highlightDateControl();
    }

    scrollToActions() {
        const searchSectionPosition =
            this.wcProductActions.getBoundingClientRect().top;

        window.scrollBy({
            top: searchSectionPosition - 60,
            behavior: 'smooth',
        });
    }
}
